//src/admin/pages/AdminAddOrders.js
import React, { useEffect, useState, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import { AdminLanguageContext } from "../context/AdminLanguageContext";
import {
  getAdminProducts,
} from "../../services/adminProducts.api";
import { createAdminOrder } from "../../services/adminOrders.api";
import { getAdminShippingSettings } from "../../services/adminShippingSettings.api";

/* ================= CONSTANTS ================= */
const LEBANON_GOVERNORATES = [
  "Beirut",
  "Mount Lebanon",
  "North",
  "South",
  "Bekaa",
  "Nabatieh",
  "Akkar",
  "Baalbek-Hermel",
];

function AdminAddOrders() {
  const navigate = useNavigate();
  const { lang } = useContext(AdminLanguageContext);

  const [isOpen, setIsOpen] = useState(false);
  const [products, setProducts] = useState([]);
  
  const [items, setItems] = useState([]);

  const [activeProduct, setActiveProduct] = useState(null);
  const [activeVariant, setActiveVariant] = useState(null);
  const [variantQty, setVariantQty] = useState(1);

  const [showConfirm, setShowConfirm] = useState(false);

  const [productSearch, setProductSearch] = useState("");
  /* ================= CUSTOMER ================= */
  const [customer, setCustomer] = useState({
    name: "",
    phone: "",
    email: "",
    governorate: "",
    area: "",
    street: "",
    building: "",
    note: "",
  });

  /* ================= ORDER ================= */
  const [order, setOrder] = useState({
  order_source: "website",
  status: "pending",
  payment_method: "cod",
  payment_status: "unpaid",
  shipping_method: "delivery",
  discount_amount: 0,
  shipping_fee: 4,
  extra_fee_label: "",
  extra_fee_amount: 0,
  notes: "",
});



  
  const availableVariants = useMemo(() => {
  if (!activeProduct) return [];

  const map = new Map();

  activeProduct.variants.forEach((v) => {
    if (v.stock_qty > 0 && !map.has(v.id)) {
      map.set(v.id, v);
    }
  });

  return Array.from(map.values());
}, [activeProduct]);


  /* ================= LOAD DATA ================= */
  useEffect(() => {
  getAdminProducts().then(setProducts);

  getAdminShippingSettings()
    .then((s) => {
      setOrder((prev) => ({
        ...prev,
        shipping_fee: Number(s.default_fee ?? 4),
      }));
    })
    .catch(() => {});
}, []);


  /* ================= CALCULATIONS ================= */
  const subtotal = useMemo(
    () => items.reduce((s, i) => s + i.unit_price * i.quantity, 0),
    [items]
  );

  const totalItems = useMemo(
    () => items.reduce((s, i) => s + i.quantity, 0),
    [items]
  );

  const discountPercent = Number(order.discount_amount || 0);
const discountValue = subtotal * (discountPercent / 100);

const shippingFee = Number(order.shipping_fee || 0);
const extraFeeAmount = Number(order.extra_fee_amount || 0);
const total = subtotal - discountValue + shippingFee + extraFeeAmount;


  // 📌 AUTO-FORMAT PHONE NUMBER (ADMIN – SAME AS CLIENT)
const formatPhone = (val) => {
  // allow international numbers untouched (except +961)
  if (val.startsWith("+") && !val.startsWith("+961")) {
    return val;
  }

  let digits = val.replace(/\D/g, "");

  // handle +961
  if (val.startsWith("+961")) {
    digits = "961" + digits.slice(3);
  }

  if (digits.startsWith("961")) {
    const rest = digits.slice(3);
    return `+961 ${rest.slice(0, 2)} ${rest.slice(2, 5)} ${rest.slice(5, 8)}`.trim();
  }

  // local numbers (0,7,8)
  if (/^[078]/.test(digits)) {
    if (digits.length <= 2) return digits;
    if (digits.length <= 5)
      return `${digits.slice(0, 2)} ${digits.slice(2)}`;
    return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)}`;
  }

  // fallback
  return val;
};


  /* ================= ADD ITEM ================= */
  const confirmAddItem = () => {
    if (!activeProduct || !activeVariant) return;

    const price = activeProduct.on_sale
      ? activeProduct.base_price *
        (1 - activeProduct.sale_percent / 100)
      : activeProduct.base_price;

    setItems((prev) => [
      ...prev,
      {
        product_id: activeProduct.id,
        variant_id: activeVariant.id,
        name_en: activeProduct.name_en,
        name_ar: activeProduct.name_ar,
        color: activeVariant.color_name,
        size: activeVariant.size,
        unit_price: Number(price),
        quantity: variantQty,
      },
    ]);

    setActiveProduct(null);
    setActiveVariant(null);
    setVariantQty(1);
  };

  

  const removeItem = (idx) => {
    setItems(items.filter((_, i) => i !== idx));
  };

  /* ================= SUBMIT ================= */
  const submit = async () => {
  if (!customer.phone) {
    alert("Phone is required");
    return;
  }

  if (!customer.name) {
    alert("Customer name is required");
    return;
  }

  if (items.length === 0) {
    alert("Add at least one product");
    return;
  }

  const cleanCustomer = {
  name: customer.name,
  phone: customer.phone,
  email: customer.email || null,
  note: customer.note || null,

  // address fields are OPTIONAL
  governorate: customer.governorate || null,
  area: customer.area || null,
  street: customer.street || null,
  building: customer.building || null,
};

await createAdminOrder({
  order_source: order.order_source,
  status: order.status,
  payment_method: order.payment_method,
  payment_status: order.payment_status,
  shipping_method: order.shipping_method,
  discount_amount: order.discount_amount,
  shipping_fee: order.shipping_fee,
  extra_fee_label: order.extra_fee_label,
  extra_fee_amount: order.extra_fee_amount,
  notes: order.notes,
  customer: cleanCustomer,
  items,
});





    navigate("/admin/orders");
  };

  /* ================= FILTER PRODUCTS ================= */
  const filteredProducts = useMemo(() => {
  if (!productSearch.trim()) return [];

  const q = productSearch.toLowerCase();

  return products.filter(
    (p) =>
      p.public_id?.toLowerCase().includes(q) ||
      p.name_en?.toLowerCase().includes(q) ||
      p.name_ar?.toLowerCase().includes(q)
  );
}, [products, productSearch]);


    const Label = ({ children }) => (
  <label className="block text-xs font-semibold text-gray-500 mb-1">
    {children}
  </label>
);


  return (
    <div className="flex bg-gray-100 min-h-screen">
      <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />
      <div className="flex-1 lg:ml-64">
        <AdminHeader setIsOpen={setIsOpen} />

        <main className="p-6 space-y-6">

    <style>
{`
  .input {
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid #d1d5db; /* gray-300 */
    border-radius: 0.75rem;
    background-color: #ffffff;
    font-size: 0.875rem;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .input:focus {
    outline: none;
    border-color: #06b6d4; /* cyan-500 */
    box-shadow: 0 0 0 2px rgba(6, 182, 212, 0.25);
  }

  .input:disabled {
    background-color: #f9fafb;
    color: #9ca3af;
    cursor: not-allowed;
  }
`}
</style>




          {/* BACK */}
          <button
  onClick={() => navigate("/admin/orders")}
  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-200 hover:bg-gray-300"
>
  ← {lang === "EN" ? "Back to Orders" : "العودة إلى الطلبات"}
</button>

<h1 className="text-3xl font-extrabold text-cyan-700 tracking-tight">
  {lang === "EN" ? "Create New Order" : "إنشاء طلب جديد"}
</h1>


          {/* ================= CUSTOMER ================= */}
          <div className="bg-white p-6 rounded-2xl shadow border space-y-4">
  <h3 className="font-bold text-cyan-700 text-lg">
    {lang === "EN" ? "Customer Information" : "معلومات الزبون"}
  </h3>

  <div className="grid md:grid-cols-2 gap-4">
    <div>
      <Label>{lang === "EN" ? "Full Name" : "الاسم الكامل"}</Label>
      <input
        className="input"
        placeholder={lang === "EN" ? "Name *" : "الاسم *"}
        value={customer.name}
        onChange={(e)=>setCustomer({...customer,name:e.target.value})}
      />
    </div>

   <div>
  <Label>{lang === "EN" ? "Phone Number" : "رقم الهاتف"}</Label>
  <input
    className="input"
    placeholder={lang === "EN" ? "Phone *" : "الهاتف *"}
    value={customer.phone}
    onChange={(e) =>
      setCustomer({
        ...customer,
        phone: formatPhone(e.target.value),
      })
    }
  />
</div>


    <div>
      <Label>{lang === "EN" ? "Email" : "البريد الإلكتروني"}</Label>
      <input
        className="input"
        placeholder={lang === "EN" ? "Optional" : "اختياري"}
        value={customer.email}
        onChange={(e)=>setCustomer({...customer,email:e.target.value})}
      />
    </div>

    <div>
      <Label>{lang === "EN" ? "Governorate" : "المحافظة"}</Label>
      <select
        className="input"
        disabled={order.shipping_method === "pickup"}
        value={customer.governorate}
        onChange={(e)=>setCustomer({...customer,governorate:e.target.value})}
      >
        <option value="">
          {lang === "EN" ? "Select Governorate" : "اختر المحافظة"}
        </option>
        {LEBANON_GOVERNORATES.map((g) => (
          <option key={g} value={g}>{g}</option>
        ))}
      </select>
    </div>

    <div>
      <Label>{lang === "EN" ? "Area" : "المنطقة"}</Label>
      <input
        className="input"
        disabled={order.shipping_method === "pickup"}
        value={customer.area}
        onChange={(e)=>setCustomer({...customer,area:e.target.value})}
      />
    </div>

    <div>
      <Label>{lang === "EN" ? "Street" : "الشارع"}</Label>
      <input
        className="input"
        value={customer.street}
        disabled={order.shipping_method === "pickup"}
        onChange={(e)=>setCustomer({...customer,street:e.target.value})}
      />
    </div>

    <div>
      <Label>{lang === "EN" ? "Building" : "المبنى"}</Label>
      <input
        className="input"
        disabled={order.shipping_method === "pickup"}
        value={customer.building}
        onChange={(e)=>setCustomer({...customer,building:e.target.value})}
      />
    </div>

    <div>
      <Label>{lang === "EN" ? "Note" : "ملاحظة"}</Label>
      <input
        className="input"
        value={customer.note}
        onChange={(e)=>setCustomer({...customer,note:e.target.value})}
      />
    </div>
  </div>
</div>


          {/* ================= CATEGORY DROPDOWN ================= */}
          <div className="bg-white p-6 rounded-2xl shadow border space-y-4">
  <h3 className="font-bold text-cyan-700 text-lg">
    {lang === "EN" ? "Select Products" : "اختر المنتجات"}
  </h3>
            {/* ================= PRODUCT SEARCH ================= */}
<div className="bg-white p-6 rounded-2xl shadow border space-y-4">
  <h3 className="font-bold text-cyan-700 text-lg">
    {lang === "EN" ? "Search Products" : "البحث عن المنتجات"}
  </h3>

  <input
    className="input"
    placeholder={
      lang === "EN"
        ? "Search by product ID or name..."
        : "بحث بالرمز أو اسم المنتج..."
    }
    value={productSearch}
    onChange={(e) => setProductSearch(e.target.value)}
  />

  {productSearch && filteredProducts.length === 0 && (
    <p className="text-sm text-gray-500">
      {lang === "EN" ? "No products found" : "لا توجد نتائج"}
    </p>
  )}

  {filteredProducts.map((p) => (
    <div
      key={p.id}
      onClick={() => {
        setActiveProduct(p);
        setActiveVariant(null);
        setVariantQty(1);
      }}
      className="flex items-center gap-3 p-3 border rounded-xl cursor-pointer
                 hover:bg-cyan-50 hover:border-cyan-400 transition"
    >
      <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-100 border">
        {p.image && (
          <img
            src={p.image}
            alt=""
            className="w-full h-full object-cover"
          />
        )}
      </div>

      <div className="text-sm">
        <div className="font-semibold">{p.public_id}</div>
        <div className="text-gray-600">
          {lang === "EN" ? p.name_en : p.name_ar}
        </div>
      </div>
    </div>
  ))}
</div>

          </div>

          {/* ================= SELECTED ITEMS ================= */}
          {items.length > 0 && (
            <div className="bg-white p-6 rounded-xl shadow">
              <h3 className="font-bold text-cyan-700 text-lg mb-4">
  {lang === "EN" ? "Selected Items" : "المنتجات المختارة"}
</h3>

            {items.map((i, idx) => {
  const product = products.find(p => p.id === i.product_id);

  return (
    <div
      key={`${i.product_id}-${i.variant_id}-${idx}`}
      className="p-4 border rounded-xl mb-3 bg-gray-50"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-100 border">
            {product?.image && (
              <img
                src={product.image}
                alt=""
                className="w-full h-full object-cover"
              />
            )}
          </div>

          <div className="text-sm">
            <div className="font-semibold">
              {product?.public_id}
            </div>
            <div className="text-gray-600">
              {i.color}/{i.size} × {i.quantity}
            </div>
          </div>
        </div>

        <button
          onClick={() => removeItem(idx)}
          className="w-8 h-8 flex items-center justify-center rounded-full
                     text-red-600 hover:bg-red-100 transition"
        >
          ✕
        </button>
      </div>
    </div>
  );
})}

            </div>
          )}

          {/* ================= ORDER SOURCE ================= */}
<div className="bg-white p-6 rounded-xl shadow">
<h3 className="font-bold text-cyan-700 text-lg mb-2">
  {lang === "EN" ? "Order Source" : "مصدر الطلب"}
</h3>

  <select
    className="input"
    value={order.order_source}
    onChange={(e) =>
      setOrder({ ...order, order_source: e.target.value })
    }
  >
    <option value="website">Website</option>
    <option value="whatsapp">WhatsApp</option>
    <option value="store">Store</option>
  </select>
</div>

{/* ================= SHIPPING METHOD ================= */}
<div className="bg-white p-6 rounded-xl shadow">
  <h3 className="font-bold text-cyan-700 text-lg mb-3">
    {lang === "EN" ? "Shipping Method" : "طريقة الشحن"}
  </h3>

  <div className="flex gap-6">
    <label className="flex items-center gap-2 text-sm font-medium">
      <input
        type="radio"
        checked={order.shipping_method === "delivery"}
        onChange={() =>
          setOrder((prev) => ({
            ...prev,
            shipping_method: "delivery",
            shipping_fee: prev.shipping_fee || 4,
          }))
        }
      />
      {lang === "EN" ? "Delivery" : "توصيل"}
    </label>

    <label className="flex items-center gap-2 text-sm font-medium">
      <input
        type="radio"
        checked={order.shipping_method === "pickup"}
        onChange={() =>
          setOrder((prev) => ({
            ...prev,
            shipping_method: "pickup",
            shipping_fee: 0, // ✅ auto zero
          }))
        }
      />
      {lang === "EN" ? "Pickup from store" : "استلام من المتجر"}
    </label>
  </div>
</div>



          {/* ================= TOTAL ================= */}
<div className="bg-white p-6 rounded-2xl shadow border border-gray-100">
  <div className="flex items-center justify-between mb-4">
    <h3 className="font-bold text-cyan-700 text-lg">
      {lang === "EN" ? "Order Summary" : "ملخص الطلب"}
    </h3>

    <span className="text-xs px-3 py-1 rounded-full bg-cyan-50 text-cyan-700 font-semibold">
      {totalItems} {lang === "EN" ? "items" : "قطعة"}
    </span>
  </div>

  <div className="space-y-3">
    {/* Subtotal */}
    <div className="grid grid-cols-[1fr_auto] items-center text-sm">
  <span className="text-gray-500">
    {lang === "EN" ? "Subtotal" : "المجموع"}
  </span>
  <span className="font-semibold text-gray-800 text-right">
    ${subtotal.toFixed(2)}
  </span>
</div>


    {/* Shipping */}
    <div className="flex items-center justify-between gap-3">
      <div className="flex flex-col">
        <span className="text-sm text-gray-500">
          {lang === "EN" ? "Shipping" : "الشحن"}
        </span>
        <span className="text-[11px] text-gray-400">
          {lang === "EN"
            ? "Admin can override per order"
            : "يمكن تغييرها لكل طلب"}
        </span>
      </div>

      <div className="relative w-32">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
          $
        </span>

        <input
          type="number"
          min="0"
          step="0.01"
          value={order.shipping_fee}
          onChange={(e) =>
            setOrder({ ...order, shipping_fee: e.target.value })
          }
          className="
            w-full pl-7 pr-3 py-2 rounded-xl border border-gray-200 bg-gray-50
            text-sm font-semibold text-gray-800 text-right
            focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500
            transition
          "
        />
      </div>
    </div>

    {/* Discount */}
    <div className="flex items-center justify-between gap-3 pt-2">
      <span className="text-sm text-gray-500">
        {lang === "EN" ? "Discount" : "الخصم"}
      </span>

      <div className="flex items-center gap-2">
        <div className="relative w-28">
          <input
            type="number"
            min="0"
            max="100"
            placeholder="0"
            value={order.discount_amount}
            onChange={(e) =>
              setOrder({ ...order, discount_amount: e.target.value })
            }
            className="
              w-full pr-8 pl-3 py-2 rounded-xl border border-gray-200 bg-gray-50
              text-sm font-semibold text-gray-800 text-right
              focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500
              transition
            "
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
            %
          </span>
        </div>

        <span className="text-xs text-gray-400 whitespace-nowrap">
          − ${discountValue.toFixed(2)}
        </span>
      </div>
    </div>

    {/* Extra Fees */}
<div className="flex items-center justify-between gap-3 pt-2">
  <span className="text-sm text-gray-500">
    {lang === "EN" ? "Extra Fees" : "رسوم إضافية"}
  </span>

  <div className="flex items-center gap-2">
    <input
      type="text"
      placeholder={lang === "EN" ? "Label" : "الوصف"}
      value={order.extra_fee_label}
      onChange={(e) =>
        setOrder({ ...order, extra_fee_label: e.target.value })
      }
      className="
        w-32 px-3 py-2 rounded-xl border border-gray-200 bg-gray-50
        text-sm
      "
    />

    <div className="relative w-24">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
        $
      </span>
      <input
        type="number"
        min="0"
        step="0.01"
        value={order.extra_fee_amount}
        onChange={(e) =>
          setOrder({ ...order, extra_fee_amount: e.target.value })
        }
        className="
          w-full pl-7 pr-3 py-2 rounded-xl border border-gray-200 bg-gray-50
          text-sm font-semibold text-right
        "
      />
    </div>
  </div>
</div>


    <hr className="my-4" />

    {/* Total */}
    <div className="grid grid-cols-[1fr_auto] items-center">
  <span className="text-sm font-semibold text-gray-600">
    {lang === "EN" ? "Total" : "الإجمالي"}
  </span>

  <span className="text-2xl font-extrabold text-cyan-700 text-right">
    ${total.toFixed(2)}
  </span>
</div>
  </div>
</div>


          <button
  onClick={()=>setShowConfirm(true)}
  className="w-full md:w-auto px-8 py-3 bg-cyan-600 hover:bg-cyan-700
             text-white rounded-xl font-semibold transition"
>
  {lang==="EN" ? "Create Order" : "إنشاء الطلب"}
</button>


          {/* ================= VARIANT MODAL ================= */}
{activeProduct && (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <div className="bg-white p-5 rounded-xl w-[360px] max-h-[80vh] overflow-y-auto">

      <h3 className="font-bold mb-3 text-center">
  {lang === "EN" ? activeProduct.name_en : activeProduct.name_ar}
</h3>

      {availableVariants.length === 0 && (
        <p className="text-sm text-gray-500 text-center">
          No available variants
        </p>
      )}

      {availableVariants.map((v) => (
        <div
          key={v.id}
          className={`flex justify-between items-center p-2 border rounded mb-2 cursor-pointer transition
            ${
              activeVariant?.id === v.id
                ? "bg-cyan-50 border-cyan-600"
                : "hover:bg-gray-50"
            }
          `}
          onClick={() => {
            setActiveVariant(v);
            setVariantQty(1);
          }}
        >
          <span className="text-sm">
            {v.color_name} / {v.size}
          </span>

          <span className="text-xs text-gray-500">
            Stock: {v.stock_qty}
          </span>
        </div>
      ))}

      {activeVariant && (
        <div className="flex items-center justify-center gap-4 mt-4">
          <button
            disabled={variantQty <= 1}
            onClick={() => setVariantQty((q) => q - 1)}
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-40"
          >
            −
          </button>

          <span className="font-semibold">{variantQty}</span>

          <button
            disabled={variantQty >= activeVariant.stock_qty}
            onClick={() => setVariantQty((q) => q + 1)}
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-40"
          >
            +
          </button>
        </div>
      )}

      <div className="flex justify-end gap-3 mt-6">
        <button
          onClick={() => setActiveProduct(null)}
          className="px-4 py-2 bg-gray-200 rounded"
        >
          Cancel
        </button>
        <button
          onClick={confirmAddItem}
          disabled={!activeVariant}
          className="px-4 py-2 bg-cyan-600 text-white rounded disabled:opacity-50"
        >
          OK
        </button>
      </div>
    </div>
  </div>
)}


          {/* ================= CONFIRM MODAL ================= */}
          {showConfirm && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center">
              <div className="bg-white p-6 rounded-xl w-96">
                <h3 className="font-bold text-lg mb-3">
  {lang === "EN" ? "Confirm Order" : "تأكيد الطلب"}
</h3>
                <p><b>{customer.name}</b></p>
                <p>{customer.phone}</p>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={()=>setShowConfirm(false)}
                    className="px-4 py-2 bg-gray-200 rounded"
                  >
                    {lang === "EN" ? "Cancel" : "إلغاء"}
                  </button>
                  <button
                    onClick={submit}
                    className="px-4 py-2 bg-cyan-600 text-white rounded"
                  >
                    {lang === "EN" ? "Confirm" : "تأكيد"}
                  </button>
                </div>
              </div>
            </div>
          )}

        </main>
      </div>
    </div>
  );
}

export default AdminAddOrders;
